import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider
} from '@mui/material';
import { Security as SecurityIcon, Rocket as RocketIcon } from '@mui/icons-material';
import { useLicense, PRICING } from '../context/LicenseContext';
import { IS_WEB } from '../services/platform';
import { EmailVerificationPanel } from './EmailVerification';

// License state machine UI: start a trial, buy a plan (opens the browser
// checkout), or activate with an existing key. While waiting for a payment it
// polls the license server so activation completes hands-free.
const LicensePrompt = () => {
  const { state, promptOpen, closePrompt, startTrial, activate, recheck, reload, buy } = useLicense();
  const [licenseKey, setLicenseKey] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [awaitingPayment, setAwaitingPayment] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const pollRef = useRef(null);

  const mode = state ? state.mode : 'loading';
  const isUnlicensed = mode === 'unlicensed';
  const isTrialExpired = mode === 'readonly' && state?.reason === 'trial-expired';
  const isPaid = mode === 'full';
  const needsVerification = IS_WEB && isUnlicensed && !state?.account?.emailVerified;

  useEffect(() => {
    if (!promptOpen) setShowPlans(false);
  }, [promptOpen]);

  // Close automatically once a PAID license lands — activation succeeded, or a
  // purchase was confirmed by the background poll. Trial and past_due users
  // must NOT auto-close (they open this screen precisely to buy/upgrade).
  useEffect(() => {
    if (promptOpen && isPaid) {
      setSuccess(IS_WEB ? 'Paid access active — enjoy!' : 'License active — enjoy!');
      const t = setTimeout(() => { closePrompt(); setSuccess(''); setAwaitingPayment(false); }, 1200);
      return () => clearTimeout(t);
    }
  }, [isPaid, promptOpen, closePrompt]);

  // While the prompt is open and a payment may be in flight, quietly poll the
  // server — the machine hash travels with the checkout, so the server can
  // activate this install the moment Payfast confirms payment.
  useEffect(() => {
    if (promptOpen && awaitingPayment && (window.electronAPI || IS_WEB)) {
      pollRef.current = setInterval(() => { recheck().catch(() => {}); }, 20000);
      return () => clearInterval(pollRef.current);
    }
    return undefined;
  }, [promptOpen, awaitingPayment, recheck]);

  if (!promptOpen || !state) return null;

  const run = async (label, fn) => {
    setBusy(label);
    setError('');
    setSuccess('');
    try {
      const result = await fn();
      if (result && result.success === false) {
        setError(result.error || 'Something went wrong. Please try again.');
      }
      return result;
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      return null;
    } finally {
      setBusy('');
    }
  };

  const handleTrial = async () => {
    const r = await run('trial', () => startTrial());
    // Starting a trial is a deliberate choice — close the prompt on success.
    if (r && r.success !== false) {
      closePrompt();
    }
  };

  const handleActivate = (e) => {
    e.preventDefault();
    if (!licenseKey.trim()) {
      setError('Please enter your license key.');
      return;
    }
    run('activate', () => activate(licenseKey.trim()));
  };

  const handleBuy = async (plan) => {
    setAwaitingPayment(true);
    setError('');
    const result = await run('buy-' + plan, () => buy(plan));
    // No checkout tab actually opened (blocked popup, network error) — don't
    // sit in "waiting for payment" polling for a purchase that never started.
    if (!result || result.success === false) setAwaitingPayment(false);
  };

  const heading = isTrialExpired
    ? 'Your 7-day free trial has ended'
    : mode === 'readonly'
      ? (IS_WEB ? 'Your plan is inactive' : 'Your license is inactive')
      : needsVerification ? 'Verify your email to start' : isUnlicensed ? 'Your free trial is ready' : 'Choose your plan';

  const subheading = isTrialExpired
    ? 'Your birds are safe. Choose a plan to keep building your pedigrees.'
    : mode === 'readonly'
      ? 'Your data is safe and always exportable. Reactivate to continue where you left off.'
      : mode === 'trial'
        ? 'Your free trial is active. Keep creating pedigrees, or choose a paid plan for more birds and PDF downloads.'
        : 'Start with a free 7-day trial — 60 birds and 3 pedigree PDF downloads included.';

  return (
    <Dialog
      open
      maxWidth={isUnlicensed && !showPlans ? 'xs' : 'md'}
      fullWidth
      aria-labelledby="license-prompt-title"
      onClose={() => { if (!busy) closePrompt(); }}
      PaperProps={{ sx: { borderRadius: 2, m: { xs: 2, sm: 4 }, width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' } } }}
    >
      <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
        <Box textAlign="center" mb={3}>
          <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
            <SecurityIcon sx={{ fontSize: 28, color: 'primary.main', mr: 1 }} />
            <Typography variant="h6" component="div" color="primary.main" fontWeight="bold">
              Pigeon Pedigree Manager
            </Typography>
          </Box>
          <Typography variant="h5" id="license-prompt-title">{heading}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {subheading}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {awaitingPayment && !isPaid && (
          <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={18} />}>
            Complete the payment in the checkout tab — this screen unlocks automatically once
            Payfast confirms it (usually under a minute).{' '}
            <Button size="small" onClick={() => run('recheck', () => recheck())} disabled={Boolean(busy)}>
              I've paid — check now
            </Button>
          </Alert>
        )}

        {/* Trial (only before a trial has ever started on this machine) */}
        {needsVerification && (
          <EmailVerificationPanel email={state.account.email} onVerified={reload} initialDelivery={state.verification} />
        )}
        {isUnlicensed && !needsVerification && (
          <Box textAlign="center" mb={3}>
            <Typography sx={{ mb: 2 }}>
              Start your trial, then add your first pigeon to create its pedigree.
              You can add parents and photos as you go.
            </Typography>
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={busy === 'trial' ? <CircularProgress size={18} color="inherit" /> : <RocketIcon />}
              onClick={handleTrial}
              disabled={Boolean(busy)}
              sx={{ minHeight: 48, py: 1.2 }}
            >
              {busy === 'trial' ? 'Starting your trial…' : 'Start my free trial'}
            </Button>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              {IS_WEB
                ? 'No card required. No automatic charge. Your 7 days start now.'
                : 'No card required. Requires a brief internet connection to register the trial.'}
            </Typography>
          </Box>
        )}

        {isUnlicensed && (
          <Box textAlign="center">
            <Button onClick={() => setShowPlans(!showPlans)} disabled={Boolean(busy)} sx={{ minHeight: 44 }}>
              {showPlans ? 'Hide paid plans' : 'Compare paid plans'}
            </Button>
            <Button onClick={closePrompt} disabled={Boolean(busy)} sx={{ minHeight: 44 }}>
              Explore first
            </Button>
          </Box>
        )}

        {mode === 'trial' && (
          <Button fullWidth variant="contained" onClick={closePrompt} disabled={Boolean(busy)} sx={{ minHeight: 48 }}>
            Continue using my free trial
          </Button>
        )}

        {(!isUnlicensed || showPlans) && <>
        <Divider sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {isUnlicensed ? 'or choose a plan' : 'Choose a plan'}
          </Typography>
        </Divider>

        <Grid container spacing={2} mb={2}>
          {PRICING.map((plan) => (
            <Grid item xs={12} sm={4} key={plan.id}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderColor: plan.highlight ? 'primary.main' : undefined,
                  borderWidth: plan.highlight ? 2 : 1,
                  position: 'relative',
                  // MUI Card clips overflow by default, which would cut off the
                  // "Best value" chip poking above the card via negative top.
                  overflow: 'visible'
                }}
              >
                {plan.highlight && (
                  <Chip
                    label="Best value"
                    color="primary"
                    size="small"
                    sx={{ position: 'absolute', top: -10, right: 12 }}
                  />
                )}
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight="bold">{plan.name}</Typography>
                  <Typography variant="h4" color="primary.main" fontWeight="bold" sx={{ my: 0.5 }}>
                    {plan.price}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{plan.per}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    {plan.note}
                  </Typography>
                  <Button
                    fullWidth
                    variant={plan.highlight ? 'contained' : 'outlined'}
                    onClick={() => handleBuy(plan.id)}
                    disabled={Boolean(busy)}
                  >
                    {plan.id === 'lifetime' ? 'Buy once' : 'Subscribe'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        </>}

        {mode === 'readonly' && (
          <Button fullWidth onClick={closePrompt} disabled={Boolean(busy)} sx={{ minHeight: 44 }}>
            Back to my birds
          </Button>
        )}

        {!IS_WEB && (
          <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">Already have a license key?</Typography>
            </Divider>

            <form onSubmit={handleActivate}>
              <Box display="flex" gap={1} maxWidth={520} mx="auto">
                <TextField
                  fullWidth
                  size="small"
                  label="License key"
                  placeholder="PPM-XXXX-XXXX-XXXX-XXXX"
                  value={licenseKey}
                  onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
                  disabled={Boolean(busy)}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={Boolean(busy) || !licenseKey.trim()}
                  startIcon={busy === 'activate' ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  Activate
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" sx={{ mt: 1 }}>
                A license can be active on 2 of your own devices at the same time.
              </Typography>
            </form>
          </>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default LicensePrompt;
